import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Check, X, Sparkles, ArrowRight, ArrowLeft } from 'lucide-react';
import { useSetup } from '@/contexts/SetupContext';
import { setupService } from '@/services/setupService';
import { setupChecklistService } from '@/services/setupChecklistService';
import { SETUP_SECTIONS_BY_ID } from '@/lib/setupChecklist';
import { SETUP_MODULES_BY_KEY } from '@/lib/setupModules';
import { useModuleStops } from '@/hooks/useModuleStops';
import SpotlightTour from './SpotlightTour';
import { scrollAppToTop } from '@/lib/scrollAppToTop';

export default function SetupBar() {
  const { run, currentModule, nextStop, prevStop, canGoBack, advanceRun, exitRun, endSectionTour } = useSetup();
  const [tourOpen, setTourOpen] = useState(false);
  // Tour pages minus any page the store switched off (same list SetupContext walks)
  const moduleStops = useModuleStops();
  // A scoped tour run by the leader a section was handed to. Same walkthrough,
  // but it ends on their checklist instead of resolving the module — that write
  // is admin-only, and touring a page isn't the same as doing the work on it.
  const isSectionTour = run?.mode === 'section';

  const subIndex = run?.subIndex ?? 0;

  // Auto-play the page tour the first time each page opens in the wizard —
  // this is the "show them how it works" moment on every stop.
  useEffect(() => {
    if (!currentModule) return;
    const steps = moduleStops(currentModule)[subIndex]?.tourSteps;
    const seenKey = `ldg-tour-${currentModule}-${subIndex}`;
    if (steps?.length && !sessionStorage.getItem(seenKey)) {
      sessionStorage.setItem(seenKey, '1');
      setupService.trackEvent('tour_opened', currentModule, { subIndex });
      setTourOpen(true);
    }
  }, [currentModule, subIndex, moduleStops]);

  // Finishing a section: mark it resolved and move on. No keep/own/fresh gate —
  // content is pre-seeded; "send us your own" lives on the Getting Started hub.
  const doneMutation = useMutation({
    mutationFn: async () => {
      if (isSectionTour) return setupChecklistService.markTourDone(run!.sectionId!);
      return setupService.updateModule(currentModule!, 'kept_templates');
    },
    onSuccess: () => {
      if (isSectionTour) {
        setupService.trackEvent('section_tour_finished', currentModule!, { sectionId: run?.sectionId });
        endSectionTour();
        return;
      }
      setupService.trackEvent('section_kept', currentModule!);
      advanceRun();
    },
    onError: () => toast.error('Something went wrong — try again.')
  });

  if (!run || !currentModule || currentModule === 'team_roster') return null;
  const info = SETUP_MODULES_BY_KEY[currentModule];
  const stops = moduleStops(currentModule);
  if (!info || !stops.length) return null; // stale/unknown module key must never crash the shell
  const stop = stops[Math.min(subIndex, stops.length - 1)];
  const isLastStop = subIndex >= stops.length - 1;
  const nextStopLabel = !isLastStop ? stops[subIndex + 1]?.label : null;
  const sectionNum = run.index + 1;
  const totalSections = run.order.filter((k) => k !== 'team_roster').length;

  return (
    <>
      <div data-setup-bar className="sticky top-0 min-[938px]:top-[calc(4rem+env(safe-area-inset-top))] z-40 bg-white border-b-2 border-[#E51636] shadow-sm px-4 py-3">
        <style>{'@keyframes ldg-next-pulse{0%,100%{box-shadow:0 0 0 0 rgba(229,22,54,0.45)}50%{box-shadow:0 0 0 7px rgba(229,22,54,0)}}'}</style>
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-[#E51636] uppercase tracking-wide">
              {isSectionTour
                // "Section 1 of 1" is meaningless on a scoped tour — name the
                // section they were handed instead.
                ? `Your section — ${SETUP_SECTIONS_BY_ID[run.sectionId!]?.label ?? info.label}`
                : `Setup — Section ${sectionNum} of ${totalSections}: ${info.label}`}
              {stops.length > 1 && ` · ${stop.label} (${Math.min(subIndex + 1, stops.length)}/${stops.length})`}
            </p>
            <p className="text-sm text-gray-600 line-clamp-2 md:truncate">{stop.nudge}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {canGoBack && (
              <Button size="sm" variant="ghost" className="text-gray-500" onClick={prevStop}>
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
            )}
            {(stop.tourSteps?.length ?? 0) > 0 && (
              <Button size="sm" variant="ghost" className="text-[#E51636]" onClick={() => { setupService.trackEvent('tour_opened', currentModule, { replay: true }); setTourOpen(true); }}>
                <Sparkles className="w-4 h-4 mr-1" /> Show me around
              </Button>
            )}
            {/* The one obvious way forward — always the biggest, pulsing element. */}
            {!isLastStop ? (
              <Button
                className="font-semibold px-5 h-10 rounded-xl shadow-sm"
                style={{ animation: 'ldg-next-pulse 1.8s ease-in-out infinite' }}
                onClick={nextStop}
              >
                Next: {nextStopLabel} <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            ) : (
              <Button
                className="font-semibold px-5 h-10 rounded-xl shadow-sm"
                style={{ animation: 'ldg-next-pulse 1.8s ease-in-out infinite' }}
                onClick={() => doneMutation.mutate()}
                disabled={doneMutation.isPending}
              >
                <Check className="w-4 h-4 mr-1.5" />
                {isSectionTour ? 'Got it — my checklist' : 'Got it — next section'}
              </Button>
            )}
            <button
              className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1 ml-1"
              onClick={exitRun}
            >
              <X className="w-3 h-3" /> Finish later
            </button>
          </div>
        </div>
      </div>

      {tourOpen && (stop.tourSteps?.length ?? 0) > 0 && (
        <SpotlightTour
          steps={stop.tourSteps!}
          onClose={() => {
            setTourOpen(false);
            // Tours often end deep in the page — reset to the top so the page
            // and the pulsing Next button read as a fresh start.
            scrollAppToTop(true);
          }}
          onEnd={(completed) => setupService.trackEvent(completed ? 'tour_completed' : 'tour_skipped', currentModule, { subIndex })}
        />
      )}
    </>
  );
}
